import { z } from 'zod'

export const VALID_STATUSES = ['open', 'fixed', 'wontfix', 'invalid'] as const
export const BUG_DEDUP_STATUSES = ['open', 'wontfix', 'invalid'] as const
export const BUG_STATUS_GUIDANCE = [
  'open: an unresolved issue; match recurring findings to this report.',
  'fixed: a fix was applied and the issue should not recur; ignore this report during deduplication and assess any recurrence as a fresh report.',
  'invalid: the report has a problem and is discarded; keep it as a duplicate candidate so the same invalid claim is not filed again. A genuinely different defect needs its own evidence and report.',
  'wontfix: the issue is acknowledged and will recur, but new bugs should not be filed for it; match recurring findings to this report.',
  'Open, wontfix and invalid are duplicate candidates. Wontfix and invalid both suppress repeat filings; neither means a fix was applied.',
].join(' ')

export type BugStatus = typeof VALID_STATUSES[number]

export const BugStatusUpdateSchema = z.object({
  status: z.enum(VALID_STATUSES),
  wontfix_reason: z.string().trim().min(1).nullable().optional(),
}).refine(data => data.wontfix_reason === undefined || data.status === 'wontfix', {
  message: 'wontfix_reason can only be supplied with status "wontfix"',
  path: ['wontfix_reason'],
})

export const USER_SETTABLE_STATUSES = VALID_STATUSES

export const RESOLVED_STATUSES = ['fixed', 'wontfix', 'invalid'] as const
export const OPEN_STATUSES = ['open'] as const
const RESOLVED = new Set<string>(RESOLVED_STATUSES)
const OPEN = new Set<string>(OPEN_STATUSES)

/** RESOLVED_STATUSES as a quoted SQL list, for the queries that build their WHERE clause as text. */
export const RESOLVED_STATUSES_SQL = RESOLVED_STATUSES.map(s => `'${s}'`).join(', ')

export function isResolved(status: string | null | undefined): boolean {
  return RESOLVED.has(status ?? '')
}

export function isOpen(status: string | null | undefined): boolean {
  return OPEN.has(status ?? '')
}

export function isWebhookBlocked(status: string | null | undefined): boolean {
  return RESOLVED.has(status ?? '')
}

export function isBugResolvedForDisplay(status: string | null | undefined): boolean {
  return status === 'fixed'
}

/** A discarded report: a user ruled it not valid, so it establishes no defect. */
export const DISCARDED_STATUSES = ['invalid'] as const
/** Settled without a fix. */
export const SETTLED_UNFIXED_STATUSES = ['wontfix', ...DISCARDED_STATUSES] as const
const DISCARDED = new Set<string>(DISCARDED_STATUSES)
const SETTLED_UNFIXED = new Set<string>(SETTLED_UNFIXED_STATUSES)

/** Q1 (docs/bug-management/authoring.md): the finding counts as a found bug. An absent status is no ruling. */
export function isFoundBug(status: string | null | undefined): boolean {
  return !DISCARDED.has(status ?? '')
}

/**
 * Q2 (docs/bug-management/authoring.md): the finding is surfaced to the user. Distinct from isBugResolvedForDisplay, which claims the stronger
 * "this was fixed" (a green check): a settled report does not claim a fix.
 */
export function isSurfacedFinding(status: string | null | undefined): boolean {
  return !SETTLED_UNFIXED.has(status ?? '')
}

export const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  fixed: 'Fixed',
  wontfix: "Won't Fix",
  invalid: 'Invalid',
}

export const STATUS_TOOLTIPS: Record<string, string> = {
  open: 'Open — bug has been reported and is awaiting a fix',
  fixed: 'Fixed — a fix has been applied',
  wontfix: "Won't Fix — bug is valid but will not be addressed",
  invalid: 'Invalid — bug report was not valid',
}

export const ALL_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  ...VALID_STATUSES.map(s => ({ value: s, label: STATUS_LABELS[s] ?? s })),
]
